import React, { useEffect, useState, useRef } from 'react';
import { ChevronDown16, Checkmark16 } from '@carbon/icons-react';
import { Loading } from 'carbon-components-react';
import { addAlert, removeAlertByGroup } from 'redux/alerts/actions';
import { useDispatch, connect, useSelector } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import { fetchSelectSessionOrgRole } from 'redux/auth/action';
import { setUserOrg } from 'redux/session/actions';
import { CFCProps, USERSROLE } from 'types';
import { sortBy } from 'lodash';
import { SCREEN_SIZE } from 'types';
import { selectScreenSize } from 'redux/session/selectors';
import {
  HeaderMenu,
  HeaderMenuItem,
  HeaderNavigation
} from 'carbon-components-react/lib/components/UIShell';

const MenuContent = (text: string) => (
  <>
    <span className="bx--text-truncate--end" title={text}>
      {text}
    </span>
    <ChevronDown16 className="bx--header__menu-arrow" />
  </>
);

function completeOrgSwitchIfReady({
  switching,
  pendingOrgId,
  loginOrgId,
  loginRole,
  loginError,
  sessionOrgId,
  sessionRole,
  dispatch,
  setSwitching,
  clearPendingOrgId
}: {
  switching: boolean;
  pendingOrgId: number | null;
  loginOrgId?: number;
  loginRole?: string;
  loginError?: unknown;
  sessionOrgId?: number;
  sessionRole?: string;
  dispatch: ReturnType<typeof useDispatch>;
  setSwitching: (value: boolean) => void;
  clearPendingOrgId: () => void;
}) {
  if (!switching) {
    return;
  }

  if (loginError) {
    setSwitching(false);
    clearPendingOrgId();
    return;
  }

  const switchedToPending =
    pendingOrgId != null &&
    (loginOrgId === pendingOrgId || sessionOrgId === pendingOrgId);

  if (!switchedToPending || pendingOrgId == null) {
    return;
  }

  setTimeout(() => dispatch(removeAlertByGroup('org-switcher-alert')), 2002);
  setSwitching(false);
  clearPendingOrgId();
  dispatch(
    addAlert(
      'org-switcher-alert',
      'info',
      'Organisation was changed',
      undefined,
      { timeout: 2000 }
    )
  );

  const nextRole = loginRole || sessionRole;
  if (
    sessionOrgId !== pendingOrgId &&
    typeof loginOrgId === 'number' &&
    nextRole
  ) {
    dispatch(setUserOrg(loginOrgId, nextRole));
  }
}

const OrganisationSwitcher: React.FC<Props> = ({
  user,
  loginData,
  loginError
}) => {
  const screenSize = useSelector(selectScreenSize);
  const [orgList, setOrgList] = useState<any[]>([]);
  const [orgName, setOrgName] = useState<string>('');
  const [switching, setSwitching] = useState<boolean>(false);
  const orgIdRef = useRef<number>(0);
  const pendingOrgIdRef = useRef<number | null>(null);
  const dispatch = useDispatch();

  const switchOrg = (id: number) => {
    if (id === user?.Organisation?.id) {
      return;
    }
    pendingOrgIdRef.current = id;
    setSwitching(true);
    dispatch(fetchSelectSessionOrgRole(id));
  };

  useEffect(() => {
    if (
      user?.Organisation &&
      (user?.role === USERSROLE.AOADMIN || user?.role === USERSROLE.AOVIEWER)
    ) {
      if (Array.isArray(user?.Organisations)) {
        setOrgList(sortBy(user.Organisations, ['name']));
      }
      orgIdRef.current = user.Organisation.id;
      setOrgName(`${user.Organisation.name} (${user.Organisation.utilityId})`);
    }
  }, [user]);

  useEffect(() => {
    completeOrgSwitchIfReady({
      switching,
      pendingOrgId: pendingOrgIdRef.current,
      loginOrgId: loginData?.organisationId,
      loginRole: loginData?.role,
      loginError,
      sessionOrgId: user?.Organisation?.id,
      sessionRole: user?.role,
      dispatch,
      setSwitching,
      clearPendingOrgId: () => {
        pendingOrgIdRef.current = null;
      }
    });
  }, [loginData, loginError, user, switching, dispatch]);

  return (
    <>
      {switching && (
        <Loading withOverlay={true} data-testid="org-switcher-loading" />
      )}
      {orgName && (
        <>
          {orgList.length > 1 ? (
            <HeaderNavigation aria-label="orgSwitcher" className="org-switcher">
              <HeaderMenu
                aria-label="orgSwitcherMenu"
                menuLinkName=""
                renderMenuContent={() => MenuContent(orgName)}
              >
                {orgList.map((org, inx) => (
                  <HeaderMenuItem key={inx} onClick={() => switchOrg(org.id)}>
                    <div
                      className={`org-switcher-item${
                        org.id === user.Organisation.id ? ' selected' : ''
                      }`}
                    >
                      {org.id === user.Organisation.id && <Checkmark16 />}
                      <span>{`${org.name}${
                        org.utilityId ? ` (${org.utilityId})` : ''
                      }`}</span>
                    </div>
                  </HeaderMenuItem>
                ))}
              </HeaderMenu>
            </HeaderNavigation>
          ) : (
            <div className="org-switcher single">
              {screenSize !== SCREEN_SIZE.SM && (
                <span className="bx--text-truncate--end" title={orgName}>
                  {orgName}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
};

const mapStateToProps = ({ session, login }: RootState) => ({
  user: session.currentUser,
  loginData: login.data,
  loginError: login.error
});

const connector = connect(mapStateToProps);

type Props = CFCProps<typeof connector>;

export default React.memo(connector(OrganisationSwitcher));
